/**
 * PublishModal.tsx
 *
 * Publish Master BOM configuration to an active BOM version.
 * Spec: docs/superpowers/plans/2026-09-22-bom-master-composer-spec.md
 */
import { useEffect, useState } from 'react'
import { X, Send, AlertCircle, CheckCircle2, ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { publishMasterBomApi } from '@/services/boms/masterApiClient'
import type { MasterComboRow } from '@/services/boms/masterTypes'

interface PublishModalProps {
  isOpen: boolean
  onClose: () => void
  combos: MasterComboRow[]
  initialComboKey?: string | null
  productId: string
  onPublishSuccess: (result: any) => void
}

export function PublishModal({
  isOpen,
  onClose,
  combos,
  initialComboKey,
  productId,
  onPublishSuccess,
}: PublishModalProps) {
  const [selectedKey, setSelectedKey] = useState<string>('')
  const [editNote, setEditNote] = useState<string>('')
  const [publishing, setPublishing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [publishResult, setPublishResult] = useState<any | null>(null)

  useEffect(() => {
    if (isOpen) {
      setError(null)
      setPublishResult(null)
      setEditNote('')

      if (
        initialComboKey &&
        combos.some(
          (c) => `${c.variant_id}_${c.assembly_type}` === initialComboKey,
        )
      ) {
        setSelectedKey(initialComboKey)
      } else if (combos.length > 0) {
        setSelectedKey(`${combos[0]?.variant_id}_${combos[0]?.assembly_type}`)
      }
    }
  }, [isOpen, initialComboKey, combos])

  if (!isOpen) return null

  const currentCombo = combos.find(
    (c) => `${c.variant_id}_${c.assembly_type}` === selectedKey,
  )

  const isAlreadyActive = Boolean(currentCombo?.published_bom)
  const activeVersion = currentCombo?.published_bom

  const handlePublish = async () => {
    if (!currentCombo) return
    setPublishing(true)
    setError(null)

    try {
      const res = await publishMasterBomApi(productId, {
        variant_id: currentCombo.variant_id,
        assembly_type: currentCombo.assembly_type,
        base_version_seq: isAlreadyActive
          ? activeVersion?.version_seq
          : undefined,
        edit_note:
          editNote.trim() ||
          `Publish từ Master BOM (${currentCombo.variant_code}, ${currentCombo.assembly_label})`,
      })

      setPublishResult(res)
      onPublishSuccess(res)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Publish thất bại')
    } finally {
      setPublishing(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <Send className="h-4 w-4 text-indigo-600" />
              <span>Publish chốt phiên bản BOM chính thức</span>
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Chuyển dòng Master BOM thành phiên bản BOM đang kích hoạt (active)
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Success Result View */}
          {publishResult ? (
            <div className="space-y-4 py-2">
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-900">
                <div className="flex items-center gap-2.5 font-bold text-base text-emerald-800">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  <span>Publish BOM thành công!</span>
                </div>
                <div className="mt-2 text-xs space-y-1 text-emerald-700">
                  <p>
                    Phiên bản tạo mới:{' '}
                    <strong>{publishResult.version_label}</strong> (seq:{' '}
                    {publishResult.version_seq})
                  </p>
                  <p>
                    Áp dụng: {publishResult.wood_line_count} dòng gỗ và{' '}
                    {publishResult.hardware_line_count} dòng ngũ kim
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs">
                <span className="text-slate-600">Xem BOM vừa xuất bản:</span>
                <Link
                  to={`/purchasing/boms/${publishResult.bom_id}`}
                  className="flex items-center gap-1 font-semibold text-indigo-600 hover:text-indigo-800"
                  onClick={onClose}
                >
                  <span>Mở trang chi tiết BOM</span>
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>
          ) : (
            <>
              {error && (
                <div className="p-3.5 text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded-lg flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-rose-600 shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              {/* Select combination */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  1. Chọn tổ hợp Màu × Kết cấu để publish
                </label>
                <select
                  value={selectedKey}
                  onChange={(e) => setSelectedKey(e.target.value)}
                  disabled={publishing}
                  className="w-full text-xs rounded-lg border border-slate-300 py-2 px-3 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {combos.map((c) => {
                    const key = `${c.variant_id}_${c.assembly_type}`
                    return (
                      <option key={key} value={key}>
                        {c.combo_code} · {c.color_name} ({c.assembly_label}) — [
                        {c.wood_count} gỗ / {c.hardware_count} NK]
                      </option>
                    )
                  })}
                </select>
              </div>

              {/* Status info box */}
              {currentCombo && (
                <div className="p-3.5 rounded-lg border border-slate-200 bg-slate-50 text-xs space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Mã tổ hợp:</span>
                    <strong className="font-mono text-slate-900">
                      {currentCombo.combo_code}
                    </strong>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Số dòng áp dụng:</span>
                    <span className="font-medium text-slate-800">
                      {currentCombo.wood_count} dòng gỗ ·{' '}
                      {currentCombo.hardware_count} dòng ngũ kim
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Trạng thái hiện tại:</span>
                    {isAlreadyActive ? (
                      <span className="font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded text-xs">
                        Đang có bản active {activeVersion?.version_label} (sẽ
                        tạo V{(activeVersion?.version_seq ?? 1) + 1})
                      </span>
                    ) : (
                      <span className="font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded text-xs">
                        Chưa có BOM (sẽ tạo mới V1)
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Note input if updating existing active version */}
              {isAlreadyActive && (
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                    2. Ghi chú chỉnh sửa (bắt buộc khi nâng version)
                  </label>
                  <textarea
                    value={editNote}
                    onChange={(e) => setEditNote(e.target.value)}
                    placeholder="Ví dụ: Cập nhật độ dày mặt bàn theo thiết kế mới..."
                    rows={2}
                    disabled={publishing}
                    className="w-full text-xs rounded-lg border border-slate-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              )}

              {/* Warning if empty */}
              {currentCombo &&
                currentCombo.wood_count === 0 &&
                currentCombo.hardware_count === 0 && (
                  <div className="p-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-600 shrink-0" />
                    <span>
                      Tổ hợp này chưa có bất kỳ dòng Master nào. Không thể
                      publish cấu hình trống.
                    </span>
                  </div>
                )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-slate-200 bg-slate-50">
          <Button variant="secondary" onClick={onClose} disabled={publishing}>
            {publishResult ? 'Đóng' : 'Hủy'}
          </Button>
          {!publishResult && (
            <Button
              variant="primary"
              onClick={handlePublish}
              disabled={
                publishing ||
                !currentCombo ||
                (currentCombo.wood_count === 0 &&
                  currentCombo.hardware_count === 0)
              }
              className="flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 px-4"
            >
              <Send className="h-3.5 w-3.5" />
              <span>{publishing ? 'Đang xuất bản...' : 'Xác nhận Publish'}</span>
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
